import { useState } from 'react';

import { useMutation } from '@apollo/client';
import { t } from 'i18next';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';

import { StringMapDropdown } from '@/components';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useCreateShipmentGroupRestMutation } from '@/services/donor-scheduling';
import { CREATE_NEW_SHIPMENT_GROUP } from '@/types.gql';

export function CreateShipmentGroupDialog({
  visible,
  onHide,
  afterSave,
}: {
  visible: boolean;
  onHide: () => void;
  afterSave: (
    newShipmentGroupName: string,
    newShipmentGroupId: string,
    newShipmentGroupEstimations: number,
  ) => void;
}) {
  const [shipmentGroupName, setShipmentGroupName] = useState('');
  const [shipmentGroupEstimations, setShipmentGroupEstimations] = useState(0);

  const useRestEndpoint = useFeatureFlag('New_Shipment_Group_Not_Functioning');
  const [createNewShipmentGroup] = useMutation(CREATE_NEW_SHIPMENT_GROUP);
  const createShipmentGroupRest = useCreateShipmentGroupRestMutation();

  const clearForm = () => {
    setShipmentGroupName('');
    setShipmentGroupEstimations(0);
  };

  const handleSave = async () => {
    let newShipmentGroupId: string;

    if (useRestEndpoint) {
      const result = await createShipmentGroupRest.mutateAsync({
        name: shipmentGroupName,
        estimations: shipmentGroupEstimations,
      });
      newShipmentGroupId = result.shipmentGroupId;
    } else {
      const { data } = await createNewShipmentGroup({
        variables: {
          input: {
            name: shipmentGroupName,
            estimations: shipmentGroupEstimations,
          },
        },
      });
      newShipmentGroupId = data?.createNewShipmentGroup
        ?.shipmentGroupId as string;
    }

    afterSave(shipmentGroupName, newShipmentGroupId, shipmentGroupEstimations);
    clearForm();
    onHide();
  };

  const handleCancel = () => {
    clearForm();
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleCancel}
      header={t('embryo.addShipmentGroup')}
      footer={
        <>
          <Button label={t('common.cancel')} onClick={handleCancel} />
          <Button label={t('common.save')} onClick={handleSave} />
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label>{t('common.name')}</label>
          <InputText
            value={shipmentGroupName}
            onChange={(e) => setShipmentGroupName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label>{t('embryo.estimations')}</label>
          <StringMapDropdown
            className="w-full"
            objectTypeCode={10122}
            attributeName="tog_shipmentestimations"
            value={shipmentGroupEstimations}
            onChange={(e) => setShipmentGroupEstimations(e.value ?? 0)}
          />
        </div>
      </div>
    </Dialog>
  );
}
